import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Gift, Loader2, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { referralService } from '../services/referralService';
import { getApiErrorMessage } from '../utils/apiError';
import { formatMoney } from '../utils/formatMoney';

const sourceLabels = {
  usdt_deposit: 'เติม USDT',
  lottery_purchase: 'ลอตเตอรี่ (เดิม)',
  yiki_bet: 'ยี่กี (เดิม)',
  legacy_deposit: 'ฝาก USDT เดิม',
};

function formatRate(rate) {
  return `${Math.round(Number(rate || 0) * 100)}%`;
}

export function ReferralPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let isMounted = true;
    setIsFetching(true);
    setError('');
    referralService
      .getMySummary()
      .then((data) => {
        if (isMounted) setSummary(data);
      })
      .catch((err) => {
        if (isMounted) {
          setError(getApiErrorMessage(err, 'ไม่สามารถโหลดข้อมูล referral ได้'));
        }
      })
      .finally(() => {
        if (isMounted) setIsFetching(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]);

  const referralLink = useMemo(() => {
    if (!summary?.referralLink) return '';
    return `${window.location.origin}${summary.referralLink}`;
  }, [summary]);

  const copyReferralLink = () => {
    if (!referralLink) return;
    navigator.clipboard
      .writeText(referralLink)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      })
      .catch(() => setError('ไม่สามารถ copy link ได้'));
  };

  if (isLoading || isFetching) {
    return (
      <div className="flex justify-center py-16 text-brand-red">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Card className="mx-auto max-w-xl">
        <CardContent className="space-y-4 text-center">
          <Gift className="mx-auto h-10 w-10 text-brand-gold" />
          <h2 className="text-2xl font-bold text-brand-red">Referral</h2>
          <p className="text-gray-600">
            เข้าสู่ระบบก่อนเพื่อดู referral code และประวัติคอมมิชชันของคุณ
          </p>
          <div className="flex justify-center gap-3">
            <Link to="/login">
              <Button>เข้าสู่ระบบ</Button>
            </Link>
            <Link to="/register">
              <Button variant="secondary">สมัครสมาชิก</Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-brand-red sm:text-3xl">
          Referral
        </h2>
        <p className="mt-2 text-gray-600">
          แชร์ code ให้เพื่อน และรับ {formatRate(summary?.commissionRate)} จากยอดเติม
          USDT ที่สำเร็จ
          {summary?.isSale ? ' (อัตรา Sale)' : ''}
        </p>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="space-y-2">
            <p className="text-sm text-gray-500">Referral code</p>
            <p className="text-3xl font-bold text-brand-red">
              {summary?.referralCode || '-'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-2">
            <p className="text-sm text-gray-500">เพื่อนที่สมัครผ่านคุณ</p>
            <p className="flex items-center gap-2 text-3xl font-bold text-brand-red">
              <Users className="h-7 w-7 text-brand-gold" />
              {summary?.invitedCount || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-2">
            <p className="text-sm text-gray-500">คอมมิชชันรวม</p>
            <p className="text-3xl font-bold text-brand-red">
              {formatMoney(summary?.totalCommission)}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>ลิงก์ชวนเพื่อน</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="break-all rounded-xl bg-red-50 px-4 py-3 text-sm text-gray-700">
            {referralLink || '-'}
          </div>
          <Button disabled={!referralLink} onClick={copyReferralLink}>
            <Copy className="h-4 w-4" />
            {copied ? 'Copied' : 'Copy referral link'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>ประวัติคอมมิชชันล่าสุด</CardTitle>
        </CardHeader>
        <CardContent>
          {summary?.recentCommissions?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] text-left text-sm">
                <thead className="text-xs uppercase text-gray-500">
                  <tr>
                    <th className="py-2 pr-4">วันที่</th>
                    <th className="py-2 pr-4">ประเภท</th>
                    <th className="py-2 pr-4">ยอดเติม</th>
                    <th className="py-2 pr-4">คอมมิชชัน</th>
                    <th className="py-2 pr-4">สถานะ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-50">
                  {summary.recentCommissions.map((commission) => (
                    <tr key={commission.id}>
                      <td className="py-3 pr-4 text-gray-600">
                        {new Date(commission.createdAt).toLocaleString()}
                      </td>
                      <td className="py-3 pr-4">
                        {sourceLabels[commission.sourceType] ||
                          commission.sourceType}
                      </td>
                      <td className="py-3 pr-4">
                        {formatMoney(commission.betAmount)}
                      </td>
                      <td className="py-3 pr-4 font-semibold text-brand-red">
                        {formatMoney(commission.commissionAmount)}
                      </td>
                      <td className="py-3 pr-4">{commission.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-sm text-gray-500">
              ยังไม่มีคอมมิชชันจาก referral
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
